import { toast } from "react-toastify";
import { IDealField } from "../interfaces/IDealField";
import IDealFieldOption from "../interfaces/IDealFieldOption";
import IDealLabelOption from "../interfaces/IDealLabelOption";

export default class DealFieldsProvider {
    private dealFields: IDealField[] = [];
    private dealFieldKeys: IDealFieldKeys = {} as IDealFieldKeys;

    public initialize(dealFields: IDealField[]): void {
        this.dealFields = dealFields;
        this.dealFieldKeys = this.getDealFieldKeys(dealFields);
    }

    public getFieldKeys(): IDealFieldKeys {
        return {
            ...this.dealFieldKeys,
        };
    }

    public getNonFundOptions(): IDealFieldOption[] {
        return this.getDealFieldOptions(this.dealFieldKeys.nonFund);
    }

    public getSoldOptions(): IDealFieldOption[] {
        return this.getDealFieldOptions(this.dealFieldKeys.sold);
    }

    public getDealProfileOptions(): IDealFieldOption[] {
        return this.getDealFieldOptions(this.dealFieldKeys.dealProfile);
    }

    public getDealSourcingMethodOptions(): IDealFieldOption[] {
        return this.getDealFieldOptions(this.dealFieldKeys.sourcingMethod);
    }

    public getDealLabels(): IDealLabelOption[] {
        const dealLabelField = this.dealFields.find(d => d.options && d.name === "Label");
        return dealLabelField?.options!.map(o => ({
            id: o.id,
            label: o.label,
            color: o.color ?? "",
        })) || [];
    }

    private getDealFieldOptions(key: string | null): IDealFieldOption[] {    
        if (!key)
            return [];

        return this.dealFields.find(df => df.key === key)!.options!.map(o => ({
            id: o.id,
            label: o.label,
        }));
    }

    private getDealFieldKeys(dealFields: IDealField[]): IDealFieldKeys {
        const missingFieldNames = dealFieldInfos
            .filter(field => !field.isOptional && !dealFields.some(df => df.name === field.name))
            .map(field => field.name);

        if (missingFieldNames.length) {
            const errorMessage = `Next deal fields are missing: ${missingFieldNames.join(", ")}`;
            toast.error(errorMessage);
            throw new Error(errorMessage);
        }

        const result: Partial<IDealFieldKeys> = {};
        dealFieldInfos.forEach(field => {
            const dealField = dealFields.find(df => df.name === field.name);
            result[field.alias] = dealField?.key;
        });

        return result as IDealFieldKeys;
    }
}

const dealFieldInfos: IDealFieldInfo[] = [
    { alias: "name", name: "Title" },
    { alias: "dealStage", name: "Stage" },
    { alias: "personId", name: "Contact person" },
    { alias: "userId", name: "Owner" },
    { alias: "contactCompany", name: "Organization" },
    { alias: "createdAt", name: "Deal created" },
    { alias: "updatedAt", name: "Update time" },

    { alias: "dealShapes", name: "Deal Shapes" },
    { alias: "state", name: "State" },
    { alias: "county", name: "County" },
    { alias: "city", name: "City" },
    { alias: "propertyOwner", name: "Property Owner" },
    { alias: "address", name: "Address" },
    { alias: "lat", name: "Lat" },
    { alias: "lon", name: "Lon" },
    { alias: "OID", name: "OID" },

    { alias: "tenant", name: "Tenant" },
    { alias: "zoning", name: "Zoning" },
    { alias: "broker", name: "Broker" },
    { alias: "askingPrice", name: "Asking Price" },
    { alias: "buildingSize", name: "Building Size" },
    { alias: "landSizeAcres", name: "Land Size (Acres)" },
    { alias: "usableLand", name: "Usable Land (Acres)" },
    { alias: "marketRentBuilding", name: "Market Rent (Building)", isOptional: true },
    { alias: "marketRentLand", name: "Market Rent (Land)", isOptional: true },
    { alias: "dropboxLink", name: "Dropbox Link" },
    { alias: "parcelId", name: "Parcel ID" },
    { alias: "regridId", name: "RegridId", isOptional: true },
    { alias: "leaseCompId", name: "Lease Comp ID" },
    { alias: "saleCompId", name: "Sale Comp ID" },
    { alias: "dealProfile", name: "Deal Profile" },
    { alias: "sourcingMethod", name: "Sourcing Method" },

    { alias: "sold", name: "Sold", isOptional: true },
    { alias: "nonFund", name: "Non-Fund Deal" },
];

interface IDealFieldInfo {
    alias: keyof IDealFieldKeys;
    name: string;
    isOptional?: boolean;
}

export interface IDealFieldKeys {
    name: string;
    dealStage: string;
    personId: string;
    userId: string;
    contactCompany: string;
    createdAt: string;
    updatedAt: string;
    dealShapes: string;
    state: string;
    county: string;
    city: string;
    propertyOwner: string;
    address: string;
    lat: string;
    lon: string;
    OID: string;
    tenant: string;
    zoning: string;
    broker: string;
    askingPrice: string;
    buildingSize: string;
    landSizeAcres: string;
    usableLand: string;
    marketRentBuilding: string | null;
    marketRentLand: string | null;
    dropboxLink: string;
    parcelId: string;
    regridId: string | null;
    leaseCompId: string;
    saleCompId: string;
    dealProfile: string;
    sourcingMethod: string;
    sold: string | null;
    nonFund: string;
}
